import type { FC } from "react";

import { Card, type CardProps, CardRowSimple, LoadingSpinner } from "widgets";
import { useVisuallyLoading } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.js";
import type { Organism, Parent, Sample } from "@/graphql.ts";
import { type GetFileResourcesQuery, useGetFileResourcesQuery } from "@/modules/files/graphql/files-queries.gql.js";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { GenomeTile, SampleParentTile } from "@/ui-modules/core/tiles";
import { SampleTile } from "@/ui-modules/core/tiles/components/SampleTile.tsx";

interface FileEventCardProps extends CardProps {
  fileId: number;
}

const responseContainsData = (data?: GetFileResourcesQuery) => {
  return (
    data?.projectFileById?.samples?.length ||
    data?.projectFileById?.parents?.length ||
    data?.projectFileById?.organisms?.length
  );
};

export const FileResourcesCard: FC<FileEventCardProps> = (props) => {
  const { data, loading, error } = useGetFileResourcesQuery({ variables: { fileId: props.fileId } });
  void useError(error);

  const file = data?.projectFileById;
  const fakeLoading = useVisuallyLoading(loading, 300);

  return (
    <Card {...props} style={{ minHeight: 100, ...props.style }}>
      {fakeLoading ? (
        <LoadingSpinner />
      ) : !(loading || fakeLoading) && !responseContainsData(data) ? (
        <ContentPlaceholder>No associated resources available</ContentPlaceholder>
      ) : (
        file && (
          <>
            {file.organisms && file.organisms.length > 0 && (
              <CardRowSimple label={"Organisms"} maxWidth={580}>
                {file?.organisms?.map(
                  (organism) =>
                    organism && <GenomeTile genome={organism as Organism} key={`file-dialog-org-${organism.name}`} />,
                )}
              </CardRowSimple>
            )}
            {file.samples && file.samples.length > 0 && (
              <CardRowSimple label={"Samples"} maxWidth={580}>
                {file?.samples?.map(
                  (sample) =>
                    sample && <SampleTile sample={sample as Sample} key={`file-dialog-sample-${sample.name}`} />,
                )}
              </CardRowSimple>
            )}
            {file.parents && file.parents.length > 0 && (
              <CardRowSimple label={"Parents"} maxWidth={580}>
                {file?.parents?.map(
                  (parent) =>
                    parent && <SampleParentTile parent={parent as Parent} key={`file-dialog-sample-${parent.name}`} />,
                )}
              </CardRowSimple>
            )}
          </>
        )
      )}
    </Card>
  );
};
